/**
 * Typed wrappers around the Sprint 2 lesson endpoints.
 *
 * Backend reference: ``backend/caitapi/routers/lessons.py``. Types
 * mirror the Pydantic response models — keep in sync when those change.
 *
 * All wrappers use the `apiGet`/`apiPost` helpers so credentials,
 * content-type, and base-URL handling stay consistent.
 */
import { API_BASE_URL, apiGet, apiPost } from "./api";

// ---------------------------------------------------------------------------
// Lesson detail
// ---------------------------------------------------------------------------

export interface LearningObjective {
  id: string;
  title: string;
  description: string;
  bloom_level: string;
}

export interface KnowledgeCheckConfig {
  pool_size: number;
  passing_percent: number;
}

export interface LessonDetail {
  id: string;
  module_id: string;
  module_sequence: number;
  sequence: number;
  slug: string;
  title: string;
  estimated_minutes: number;
  article_mdx_path: string;
  video_url: string | null;
  /** WebVTT captions URL for the video (FR-O-4). Resolved server-side
   *  as ``lessons.captions_url`` || ``modules.intro_captions_url``. NULL
   *  when the video has no captions sidecar. */
  video_captions_url: string | null;
  learning_objectives: LearningObjective[];
  knowledge_check: KnowledgeCheckConfig;
}

export function getLessonBySlug(
  courseSlug: string,
  moduleSlug: string,
  lessonSlug: string,
): Promise<LessonDetail> {
  return apiGet<LessonDetail>(
    `/api/courses/${encodeURIComponent(courseSlug)}/modules/${encodeURIComponent(
      moduleSlug,
    )}/lessons/${encodeURIComponent(lessonSlug)}`,
  );
}

// ---------------------------------------------------------------------------
// Heartbeat
// ---------------------------------------------------------------------------

export interface HeartbeatPayload {
  video_pct: number;
  scroll_pct: number;
  watch_seconds_delta?: number;
}

export interface HeartbeatResponse {
  video_pct: number;
  scroll_pct: number;
  total_watch_seconds: number;
  completed_at: string | null;
}

export function sendHeartbeat(
  lessonId: string,
  payload: HeartbeatPayload,
): Promise<HeartbeatResponse> {
  return apiPost<HeartbeatResponse>(
    `/api/lessons/${encodeURIComponent(lessonId)}/heartbeat`,
    payload,
  );
}

/**
 * FIX-PLAN-2026-06-02 Bug 2.2: fire-and-forget heartbeat that survives
 * page unload via the Beacon API.
 *
 * The normal sendHeartbeat path is async and gets aborted when the user
 * navigates away / closes the tab before the throttled flush fires;
 * the unposted scroll/video pct is then lost and the lesson server-side
 * sticks at 0% even though the user actually consumed it. Beacons are
 * queued by the browser to fire AFTER the page unloads, so the server
 * gets the final state even on rapid reload / tab-close / crash.
 *
 * No response — beacons are one-way. Use only at unload time; for
 * normal in-page updates use sendHeartbeat which returns the
 * server-confirmed state.
 *
 * Returns true iff the beacon was queued by the browser (false means
 * the payload was too large or the API is unavailable in this context).
 */
export function sendHeartbeatBeacon(
  lessonId: string,
  payload: HeartbeatPayload,
): boolean {
  if (typeof navigator === "undefined" || !navigator.sendBeacon) {
    return false;
  }
  // Prepend API_BASE_URL so in production the beacon hits api.cait.academy
  // rather than the SPA origin (which doesn't have a heartbeat handler).
  // In dev API_BASE_URL is empty and the Vite proxy routes /api → backend.
  const url = `${API_BASE_URL}/api/lessons/${encodeURIComponent(lessonId)}/heartbeat`;
  // Beacons need a Blob with the right content-type so the backend's
  // JSON body parser accepts it.
  const blob = new Blob([JSON.stringify(payload)], {
    type: "application/json",
  });
  try {
    return navigator.sendBeacon(url, blob);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Knowledge check
// ---------------------------------------------------------------------------

export interface KCClientOption {
  id: string;
  text: string;
}

export interface KCClientItem {
  id: string;
  stem: string;
  options: KCClientOption[];
}

export interface StartKCResponse {
  attempt_id: string;
  items: KCClientItem[];
  passing_percent: number;
  // FIX-PLAN-2026-06-02 Bug 2.1: backend now returns the existing
  // in-flight attempt instead of 409. Frontend uses these to render a
  // "Picking up where you left off" hint and pre-select prior answers.
  resumed?: boolean;
  prior_answers?: Record<string, string>;
}

export interface AbandonKCResponse {
  abandoned_attempt_id: string | null;
}

export interface RecordAnswerResponse {
  attempt_id: string;
  answered_count: number;
  total_items: number;
}

export interface SubmitReviewOption {
  id: string;
  text: string;
  correct: boolean;
  rationale: string;
}

export interface SubmitReviewItem {
  id: string;
  stem: string;
  options: SubmitReviewOption[];
  candidate_option_id: string | null;
  correct_option_id: string;
  is_correct: boolean;
}

export interface SubmitKCResponse {
  attempt_id: string;
  score: number;
  passed: boolean;
  passing_percent: number;
  items: SubmitReviewItem[];
}

export function startKnowledgeCheck(lessonId: string): Promise<StartKCResponse> {
  return apiPost<StartKCResponse>(
    `/api/lessons/${encodeURIComponent(lessonId)}/knowledge-check/start`,
  );
}

/**
 * FIX-PLAN-2026-06-02 Bug 2.1: abandon any in-flight KC attempt for
 * this lesson. Idempotent (returns abandoned_attempt_id=null if there
 * was nothing to abandon). The next call to `startKnowledgeCheck` will
 * mint a brand-new attempt with new pool draws.
 */
export function abandonKnowledgeCheck(
  lessonId: string,
): Promise<AbandonKCResponse> {
  return apiPost<AbandonKCResponse>(
    `/api/lessons/${encodeURIComponent(lessonId)}/knowledge-check/abandon`,
  );
}

export function recordAnswer(
  attemptId: string,
  itemId: string,
  optionId: string,
): Promise<RecordAnswerResponse> {
  return apiPost<RecordAnswerResponse>(
    `/api/knowledge-check/${encodeURIComponent(attemptId)}/answer`,
    { item_id: itemId, option_id: optionId },
  );
}

export function submitKnowledgeCheck(
  attemptId: string,
): Promise<SubmitKCResponse> {
  return apiPost<SubmitKCResponse>(
    `/api/knowledge-check/${encodeURIComponent(attemptId)}/submit`,
  );
}

// ---------------------------------------------------------------------------
// Completion
// ---------------------------------------------------------------------------

export interface LessonCompletionStatus {
  lesson_id: string;
  completed: boolean;
  completed_at: string | null;
  completed_via: string | null;
  video_pct: number;
  scroll_pct: number;
  video_required: boolean;
  completion_threshold: number;
  kc_passed: boolean;
  kc_latest_attempt_id: string | null;
  kc_latest_score: number | null;
  next_lesson_id: string | null;
  /** Full slug path for the next lesson, e.g.
   *  ``/lesson/cait-foundations/global-mental-health-crisis``. Null if no
   *  next lesson exists. Lets the frontend navigate directly without a
   *  second slug-lookup call. */
  next_lesson_slug_path: string | null;
  prev_lesson_id: string | null;
  /** Mirror of next_lesson_slug_path going backward. Null at the very
   *  start of the course. Used by the lesson player's Prev nav button. */
  prev_lesson_slug_path: string | null;
  blocking_reasons: string[];
}

export function getCompletionStatus(
  lessonId: string,
): Promise<LessonCompletionStatus> {
  return apiGet<LessonCompletionStatus>(
    `/api/lessons/${encodeURIComponent(lessonId)}/completion-status`,
  );
}

export function completeLesson(
  lessonId: string,
): Promise<LessonCompletionStatus> {
  return apiPost<LessonCompletionStatus>(
    `/api/lessons/${encodeURIComponent(lessonId)}/complete`,
  );
}
